(() => {
  if (window.themeCollectionStaticAtcInit) return;
  window.themeCollectionStaticAtcInit = true;

  const selectors = {
    container: '.js-static-atc',
    trigger: '.js-static-atc-trigger',
    variantsWrap: '.js-static-atc-variants',
    variantButton: '.js-static-atc-variant',
    error: '.js-static-atc-error'
  };

  /**
   * Returns sections that can be refreshed after cart updates.
   * @returns {Element[]}
   */
  function getSectionsToUpdate() {
    return ['page-header', 'cart-drawer']
      .map((selector) => document.querySelector(selector))
      .filter((el) => el && el.dataset && el.dataset.sectionId);
  }

  /**
   * Reads a user-friendly error message from cart API response data.
   * @param {object} data - Cart API response data.
   * @returns {string}
   */
  function getCartErrorMessage(data) {
    if (!data || typeof data !== 'object') return 'Unable to add item to cart.';

    let error = typeof data.description === 'string' ? data.description : data.message;
    if (data.errors && typeof data.errors === 'object') {
      const errors = Object.values(data.errors).reduce((acc, value) => {
        if (Array.isArray(value)) {
          return acc.concat(value);
        }
        if (value) {
          acc.push(value);
        }
        return acc;
      }, []);
      if (errors.length) error = errors.join(', ');
    }

    return error || 'Unable to add item to cart.';
  }

  /**
   * Sets/clears inline ATC error state.
   * @param {Element} container - Static ATC container element.
   * @param {string | false} error - Error message or false to clear.
   */
  function setErrorState(container, error = false) {
    const errorEl = container.querySelector(selectors.error);
    if (!errorEl) return;

    if (error) {
      errorEl.textContent = error;
      errorEl.removeAttribute('hidden');
    } else {
      errorEl.textContent = '';
      errorEl.setAttribute('hidden', '');
    }
  }

  /**
   * Adds a variant to the cart and refreshes drawer/header sections.
   * @param {Element} container - Static ATC container.
   * @param {string} variantId - Variant id to add.
   * @param {Element} opener - Element that triggered the add action.
   */
  async function addVariantToCart(container, variantId, opener) {
    if (!variantId) return;

    setErrorState(container);

    const triggerBtn = container.querySelector(selectors.trigger);
    const variantButtons = Array.from(container.querySelectorAll(selectors.variantButton));

    container.classList.add('is-busy');
    if (triggerBtn) {
      triggerBtn.disabled = true;
      triggerBtn.setAttribute('aria-disabled', 'true');
    }
    variantButtons.forEach((btn) => {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
    });

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const sectionsToUpdate = getSectionsToUpdate();
    const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId);
    if (sectionIds.length) {
      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sectionIds.join(','));
    }

    try {
      const response = await fetch((window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(getCartErrorMessage(data));
      }

      if (data.sections && sectionIds.length === Object.keys(data.sections).length) {
        sectionsToUpdate.forEach((el) => {
          if (typeof el.updateFromCartChange === 'function') {
            el.updateFromCartChange(data.sections[el.dataset.sectionId]);
          }
        });
      } else {
        container.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
      }

      const cartDrawer = document.querySelector('.js-cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open(opener);
      } else {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
          detail: { opener }
        }));
      }

      container.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        cancelable: false,
        detail: {
          variantId: data.variant_id
        }
      }));
    } catch (error) {
      setErrorState(container, error.message || 'Unable to add item to cart.');
      container.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        cancelable: false,
        detail: {
          error: error.message || 'Unable to add item to cart.'
        }
      }));
      container.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
    } finally {
      container.classList.remove('is-busy');

      if (triggerBtn) {
        triggerBtn.disabled = false;
        triggerBtn.removeAttribute('aria-disabled');
      }

      variantButtons.forEach((btn) => {
        btn.disabled = btn.dataset.available !== 'true';
        btn.removeAttribute('aria-disabled');
      });
    }
  }

  document.addEventListener('click', (evt) => {
    const variantButton = evt.target.closest(selectors.variantButton);
    if (variantButton) {
      const container = variantButton.closest(selectors.container);
      if (!container || variantButton.dataset.available !== 'true') return;

      evt.preventDefault();
      addVariantToCart(container, variantButton.dataset.variantId, variantButton);
      return;
    }

    const trigger = evt.target.closest(selectors.trigger);
    if (!trigger) return;

    const container = trigger.closest(selectors.container);
    if (!container) return;

    evt.preventDefault();

    const hasVariants = trigger.dataset.hasVariants === 'true';
    if (hasVariants) {
      const variantsWrap = container.querySelector(selectors.variantsWrap);
      if (variantsWrap) {
        variantsWrap.removeAttribute('hidden');
        trigger.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    addVariantToCart(container, trigger.dataset.variantId, trigger);
  });
})();
